import { useQuery, gql, NetworkStatus } from '@apollo/client'
import { isEmpty } from 'lodash'
import { useEffect } from 'react'
import { defineMessages, useIntl, FormattedMessage } from 'react-intl'
import { NavLink, useLocation, useParams } from 'react-router-dom'

import EmptyState from 'components/v2/EmptyState'
import { Loader } from '@10xLabs/react-web/components/Loader'
import { Button } from 'components/v2/Button'
import Card from '@10xLabs/react-web/components/Card'
import { ForbiddenView } from 'features/App/components/ForbiddenView'
import { withLastUrl } from 'features/App/components/WithLastUrl'
import { InventoryHeader } from 'features/Inventories/components/InventoryHeader'
import { CartTabs } from 'features/Carts/components/Tabs'
import { InventoryContent } from 'features/Inventories/components/InventoryContent'
import { FEATURES } from 'features/Roles/components/Permissions/constants'
import { getQueries } from 'utils/route'
import { canReadCart } from 'features/Users/utils'
import { CartActivityList } from './List'
import { CartActivityPagination } from './Pagination'
import { useLogin } from 'features/Login/hooks'

const LIMIT = 30

const messages = defineMessages({
  helmet: {
    id: 'cart.activity.helmet',
    defaultMessage: 'Actividad de carrito | Nexbus Admin'
  }
})

const GET_CART = gql`
  query GetCart($cartId: ID!) {
    cart(input: { id: $cartId }) {
      id
      name
    }
  }
`

const GET_CART_ACTIVITY = gql`
  query GetCartActivity($limit: Int!, $page: Int!, $cartId: ID!) {
    cartActivity(pagination: { size: $limit, page: $page }, input: { cartID: $cartId }) {
      total
      items {
        id
        amount
        borrowedAt
        returnedAt
        conciliationStatus
        user {
          id
          username
          firstName
          lastName
        }
        sales {
          id
          amount
          status
        }
      }
    }
  }
`

const CartActivityView = (): TSFixMe => {
  let content
  const location = useLocation()
  const { cartId } = useParams()
  const intl = useIntl()

  const login = useLogin()

  useEffect((): TSFixMe => {
    document.title = intl.formatMessage(messages.helmet)
  }, [])

  if (!canReadCart(login.accessToken.policies)) {
    return <ForbiddenView feature={FEATURES.CART} />
  }

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { data: cartData } = useQuery(GET_CART, {
    variables: {
      cartId
    }
  })

  let { page = 1 } = getQueries(location.search)

  // @ts-expect-error TS(2559) FIXME Remove this comment to see the full error message
  page = parseInt(page)

  const {
    loading,
    error,
    data = {},
    refetch,
    networkStatus
    // eslint-disable-next-line react-hooks/rules-of-hooks
  } = useQuery(GET_CART_ACTIVITY, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    variables: {
      limit: LIMIT,
      page: page - 1,
      cartId
    }
  })

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect((): TSFixMe => {
    refetch()
  }, [])

  if (networkStatus === NetworkStatus.refetch || loading) {
    content = (
      <EmptyState.Container>
        <Loader />
      </EmptyState.Container>
    )
  } else if (error) {
    content = (
      <EmptyState.Container>
        <EmptyState.Description>
          <FormattedMessage
            id="spanish.cart.activity.error.description"
            defaultMessage="Hubo un error al cargar la actividad del carrito"
          />
        </EmptyState.Description>

        <EmptyState.Action>
          <Button onClick={(): TSFixMe => refetch()} kind="primary">
            <FormattedMessage id="spanish.cart.activity.error.action" defaultMessage="Recargar la actividad" />
          </Button>
        </EmptyState.Action>
      </EmptyState.Container>
    )
  } else if (isEmpty(data.cartActivity.items) && page === 1) {
    content = (
      <EmptyState.Container>
        <EmptyState.Description>
          <FormattedMessage
            id="spanish.cart.activity.empty.description"
            defaultMessage="No tienes actividades todavía"
          />
        </EmptyState.Description>
      </EmptyState.Container>
    )
  } else if (isEmpty(data.cartActivity.items)) {
    content = (
      <EmptyState.Container>
        <EmptyState.Description>
          <FormattedMessage
            id="spanish.cart.activity.empty.filters.description"
            defaultMessage="No se encontró actividades"
          />
        </EmptyState.Description>

        <EmptyState.Action>
          <Button as={NavLink} to={`/carts/${cartId}/activity`} kind="primary">
            <FormattedMessage id="spanish.cart.activity.empty.filters.action" defaultMessage="Borrar filtros" />
          </Button>
        </EmptyState.Action>
      </EmptyState.Container>
    )
  } else {
    content = (
      <>
        <CartActivityList activityItems={data.cartActivity.items} />

        <CartActivityPagination
          page={page}
          cartId={cartId}
          limit={LIMIT}
          totalItems={data.cartActivity.total}
          currentItemsCount={data.cartActivity.items.length}
        />
      </>
    )
  }

  return (
    <>
      <InventoryHeader inventory={cartData && cartData.cart} />

      <CartTabs cartId={cartId} />

      <InventoryContent>
        <Card.Container>
          <Card.Content noHeader noFooter>
            {content}
          </Card.Content>
        </Card.Container>
      </InventoryContent>
    </>
  )
}

export default withLastUrl(CartActivityView)
